import { useCallback, useMemo } from "react";
import MetadataStoreContext from "./contexts/MetadataStoreContext";
import MetadataActionsContext from "./contexts/MetadataActionsContext";
import MetadataCircuitsContext from "./contexts/MetadataCircuitsContext";
import CircuitHelpers from "helpers/CircuitHelpers";
import useExercises from "providers/ExercisesProvider/useExercises";
import { MetadataType } from "types";
import type {
  MetadataAnthropometric,
  Metadata,
  MetadataExercise,
  MetadataWellness,
  MetadataScore,
  MetadataCircuit,
} from "types";
import useWellnessMetadata from "providers/WellnessMetadataProvider/useWellnessMetadata";
import useAnthropometricMetadata from "providers/AnthropometricMetadataProvider/useAnthropometricMetadata";
import useHyroxMetadata from "providers/HyroxMetadataProvider/useHyroxMetadata";
import MetadataHelpers from "helpers/MetadataHelpers";
import { useTranslation } from "react-i18next";
import useCanDo from "hooks/useCanDo";
import useGetAllCircuitsQuery from "hooks/useGetAllCircuitsQuery";
import { circuitsEnabled } from "config/features";

interface MetadataProviderProps {
  children?: React.ReactNode;
}

function MetadataProvider(props: MetadataProviderProps) {
  const anthropometrics = useAnthropometricMetadata();
  const wellness = useWellnessMetadata();
  const exercises = useExercises();
  const canAccessHyrox = useCanDo("hyrox.any");
  const hyroxMetadata = useHyroxMetadata();
  // Circuits are gated by the build flag (off in production until ready) AND reuse
  // the custom-exercise policy (HubEnabled + CustomExercisesEnabled).
  const canAccessCircuits = useCanDo("library.any");
  const { data: circuits } = useGetAllCircuitsQuery({ enabled: circuitsEnabled && canAccessCircuits });
  const { t } = useTranslation();

  const exerciseTypeLabel = t(`global.metadata_type_${MetadataType.EXERCISE}`);
  const wellnessTypeLabel = t(`global.metadata_type_${MetadataType.WELLNESS}`);
  const anthropometricTypeLabel = t(`global.metadata_type_${MetadataType.ANTHROPOMETRIC}`);
  const scoreTypeLabel = t(`global.metadata_type_${MetadataType.SCORE}`);
  const circuitTypeLabel = t(`global.metadata_type_${MetadataType.CIRCUIT}`);

  const metadataFlatten = useMemo<Metadata[]>(() => {
    const exercisesWithType: MetadataExercise[] = exercises.map((elem) => ({
      id: elem.id,
      metadata: elem,
      typeLabel: exerciseTypeLabel,
      type: MetadataType.EXERCISE,
    }));

    const wellnessWithType: MetadataWellness[] = wellness.map((elem) => ({
      id: elem.id,
      metadata: elem,
      typeLabel: wellnessTypeLabel,
      type: MetadataType.WELLNESS,
    }));

    const anthropometricsWithType: MetadataAnthropometric[] = anthropometrics.map((elem) => ({
      id: elem.value,
      metadata: elem,
      typeLabel: anthropometricTypeLabel,
      type: MetadataType.ANTHROPOMETRIC,
    }));

    const hyroxWithType: MetadataScore[] = canAccessHyrox
      ? hyroxMetadata.map((elem) => ({
          id: elem.id,
          metadata: elem,
          typeLabel: scoreTypeLabel,
          type: MetadataType.SCORE,
        }))
      : [];

    /**
     * The order will be the order of the metadata in the UI.
     * We can change the order by changing the order of the array or sorting it.
     */
    return [...anthropometricsWithType, ...exercisesWithType, ...wellnessWithType, ...hyroxWithType];
  }, [
    exerciseTypeLabel,
    wellnessTypeLabel,
    anthropometricTypeLabel,
    scoreTypeLabel,
    anthropometrics,
    exercises,
    wellness,
    canAccessHyrox,
    hyroxMetadata,
  ]);

  /**
   * Circuits are kept out of {@link metadataFlatten} (the selectable-options store)
   * because no surface supports picking a circuit as a measure yet — but they are
   * still resolvable by id via {@link metadataMap}, so circuit measurements resolve
   * their name/metric/sortType across downstream surfaces.
   */
  const circuitMetadata = useMemo<MetadataCircuit[]>(
    () =>
      circuits.map((elem) => ({
        id: elem.id,
        metadata: elem,
        typeLabel: circuitTypeLabel,
        type: MetadataType.CIRCUIT,
      })),
    [circuits, circuitTypeLabel]
  );

  /**
   * Scored circuits (those with an input, so they carry an aggregated metric beyond
   * `roundCount`) surfaced to dashboard measure pickers. No-input circuits are excluded
   * per spec — they are not reviewable on dashboards.
   */
  const scoredCircuitMetadata = useMemo<MetadataCircuit[]>(
    () => circuitMetadata.filter((item) => !CircuitHelpers.isNoInputCircuit(item.metadata)),
    [circuitMetadata]
  );

  const metadataMap = useMemo(() => {
    return [...metadataFlatten, ...circuitMetadata].reduce(
      (acc, item) => ({ ...acc, [item.id]: item }),
      {} as Record<string, Metadata>
    );
  }, [metadataFlatten, circuitMetadata]);

  const getById = useCallback((id: string) => metadataMap[id] || null, [metadataMap]);

  const getMeasureName = useCallback(
    (id: string | null) => {
      if (!id) return null;

      const attribute = getById(id);
      return MetadataHelpers.getMetadataName(attribute);
    },
    [getById]
  );

  const getMetricLabel = useCallback(
    (id: string | null, field: string | null) => {
      if (!id) return null;

      const attribute = getById(id);
      return MetadataHelpers.getMetadataMetricLabel(attribute, field);
    },
    [getById]
  );

  const getMetric = useCallback(
    (id: string | null, field: string | null) => {
      if (!id) return null;
      const attribute = getById(id);

      return MetadataHelpers.getMetadataMetricByField(attribute, field);
    },
    [getById]
  );

  const getSortType = useCallback(
    (id: string | null, field: string | null) => {
      if (!id) return 1;

      const attribute = getById(id);

      if (attribute.type !== "Exercise" && attribute.type !== "Score" && attribute.type !== "Circuit") return 1;

      return MetadataHelpers.getMetadataMetricByField(attribute, field)?.sortType ?? 1;
    },
    [getById]
  );

  const checkHasVariants = useCallback(
    (id: string | null) => {
      if (!id) return false;

      const attribute = getById(id);
      return MetadataHelpers.hasVariants(attribute);
    },
    [getById]
  );

  const actions = useMemo(
    () => ({ getById, getMeasureName, getMetricLabel, getMetric, checkHasVariants, getSortType }),
    [getById, getMeasureName, getMetricLabel, getMetric, checkHasVariants, getSortType]
  );

  return (
    <MetadataActionsContext.Provider value={actions}>
      <MetadataCircuitsContext.Provider value={scoredCircuitMetadata}>
        <MetadataStoreContext.Provider value={metadataFlatten}>{props.children}</MetadataStoreContext.Provider>
      </MetadataCircuitsContext.Provider>
    </MetadataActionsContext.Provider>
  );
}

export default MetadataProvider;
